import React, { FC, useCallback, useContext, memo, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { StringParam, useQueryParam } from 'use-query-params';

import Sections from 'components/sections/Sections';
import TimelineSection from 'components/sections/TimelineSection';
import CreateSectionButton from 'components/buttons/CreateSectionButton';
import ColorfulSectionWrapper from 'components/events/ColorfulSectionWrapper';
import TimelineSectionsHeader from 'components/sections/TimelineSectionsHeader';
import CollapseExpandSectionsButton from 'components/buttons/CollapseExpandSectionsButton';
import { EventContext } from 'components/context/EventContext';
import { SectionsFilterableContext } from '../SectionsFilterContext';

import { useModal } from 'vibo-ui/Modal';
import { getNextSectionId } from 'components/buttons/NextSectionButton/constants';
import { getFilteredSectionsByQ, hasSectionsFilterValue } from 'services/sections/helpers';

import { SECTIONS, SECTIONS_NO_IMAGES_ALLOWED } from 'graphql/queries/events';
import { DELETE_EVENT_SECTION, REORDER_EVENT_SECTIONS } from 'graphql/mutations/events';

import { Modals } from 'types/enums';
import { EventSectionsProps } from './interfaces';
import { DeleteSectionModalProps } from 'components/modals/DeleteSectionModal';
import { ConfirmActionModalProps } from 'components/modals/ConfirmActionModal';

const EventSections: FC<EventSectionsProps> = ({
  event,
  sections,
  selectedSection,
  refetch,
  isSectionsLoading,
  onSectionClick,
  onEditClick,
}) => {
  const { t } = useTranslation();

  const { id: eventId = '' } = useParams<keyof EventPageRouteParams>();

  const [, setSectionsQ] = useQueryParam('sectionsQ', StringParam);

  const { filter, setFilter } = useContext(SectionsFilterableContext);
  const { isCoverPhotoAllowed, canAddSection = false } = useContext(EventContext);

  const { openModal } = useModal();

  const filteredSections = useMemo(() => getFilteredSectionsByQ(sections, filter?.q), [
    sections,
    filter,
  ]);

  const openDeleteSectonModal = (section: TimelineSection) =>
    openModal<DeleteSectionModalProps>({
      key: Modals.deleteSection,
      props: {
        delteSectionMutation: DELETE_EVENT_SECTION,
        refetchQueries: ['sections', 'getEvent'],
        variables: {
          eventId,
          sectionId: section._id,
        },
        onDelete: () => {
          const nextSectionId = getNextSectionId(sections, section);

          onSectionClick(sections.find(el => el._id === nextSectionId) as EventSection);
        },
      },
    });
  const openConfirmReorderingModal = () =>
    openModal<ConfirmActionModalProps>({
      key: Modals.confirmAction,
      props: {
        title: t('clearSearch'),
        children: t('clearSearchReorderSections'),
        submit: {
          text: t('clear'),
          onClick: handleConfirmReordering,
        },
      },
    });

  const [reorderSections] = useMutation<boolean, ReorderSectionsVariables>(REORDER_EVENT_SECTIONS, {
    refetchQueries: ['sections'],
  });

  const handleConfirmReordering = useCallback(() => {
    setSectionsQ(undefined);
    setFilter(null);
    refetch({ eventId, filter });
  }, [eventId, filter?.q]);

  return (
    <Sections<EventSection, EventSectionsVariables>
      sections={filteredSections}
      reorderProps={{
        // @ts-ignore
        onReorder: reorderSections,
        variables: { eventId },
        listQuery: isCoverPhotoAllowed ? SECTIONS : SECTIONS_NO_IMAGES_ALLOWED,
        listCacheKey: 'sections',
        onBeforeCapture: () => {
          if (hasSectionsFilterValue(filter)) {
            return openConfirmReorderingModal();
          }
        },
      }}
      renderSection={({ section, ...props }): JSX.Element => (
        <ColorfulSectionWrapper headlineColors={section.headlineColors}>
          <TimelineSection
            section={section}
            isSelected={section._id === selectedSection?._id}
            onClick={onSectionClick}
            onEditClick={onEditClick}
            onDeleteClick={openDeleteSectonModal}
            {...props}
          />
        </ColorfulSectionWrapper>
      )}
      renderHeader={() => (
        <TimelineSectionsHeader
          numSections={filteredSections.length}
          onSearch={q => {
            setFilter(prev => ({ ...prev, q }));
          }}
          collapseBtn={
            <CollapseExpandSectionsButton
              disabled={!filteredSections.length}
              key="expand-collapse-sections-btn"
            />
          }
          buttons={[
            canAddSection ? (
              <CreateSectionButton
                eventSettings={event.settings as EventSettings}
                tooltipProps={{
                  overlay: t('addSection'),
                  fillOverlay: true,
                  placement: 'top',
                }}
                shape="round"
                size="lg"
                key="add-section-btn"
              />
            ) : null,
          ]}
        />
      )}
      isSectionsLoading={isSectionsLoading}
    />
  );
};

export default memo(EventSections);
